import { createAction } from 'redux-actions';
import { push } from 'react-router-redux';
import isEmail from 'validator/lib/isEmail';

import { openFailedAlert, openSuccessAlert } from 'actions/alert';
import { wrapFetchAuth } from 'actions/endpoint';

import { isEmpty, convertReservationEvent } from 'util/helper';
import { languageMapRoute, t } from 'util/i18n';
import { getUser } from './user';

export const changeReservationStep = createAction('CHANGE_RESERVATION_STEP', step => ({ step }));

export const clearReservationTime = createAction('CLEAR_RESERVATION_TIME');

export const getReservationTime = createAction(
	'FETCH_RESERVATION_TIME',
	async (workDate, roomId, period = 'MORNING') => {
		const { status, message, data } = await wrapFetchAuth(
			'reservation/getDateStatus',
			{ method: 'GET' },
			{ work_date: workDate, period, room_id: roomId },
		);

		if (status !== 200) {
			throw openFailedAlert('generic', message);
		}

		return {
			reservationTimeList: {
				[period]: data.reservations.map(timeInfo => ({
					time: timeInfo.work_date,
					live: timeInfo.LIVE,
					phone: timeInfo.PHONE,
					online: timeInfo.ONLINE,
					checkin: timeInfo.CHECKIN,
					checkout: timeInfo.CHECKOUT,
					available: timeInfo.LIVE > 0 || timeInfo.PHONE > 0 || timeInfo.ONLINE > 0,
				})),
			},
		};
	},
);

export const getReservationTimeWithInterview = createAction(
	'FETCH_RESERVATION_TIME',
	async (workDate, period = 'MORNING') => {
		const { status, message, data } = await wrapFetchAuth(
			'reservation/getInterviewDateStatus',
			{ method: 'GET' },
			{ work_date: workDate, period },
		);

		if (status !== 200) {
			throw openFailedAlert('generic', message);
		}

		return {
			reservationTimeList: {
				[period]: data.reservations.map(timeInfo => ({
					time: timeInfo.work_date,
					live: 0,
					phone: 0,
					online: 0,
					checkin: 0,
					checkout: 0,
					interview_phone: timeInfo.INTERVIEW_PHONE,
					interview_online: timeInfo.INTERVIEW_ONLINE,
					available: timeInfo.INTERVIEW_PHONE > 0 || timeInfo.INTERVIEW_ONLINE > 0,
				})),
			},
		};
	},
);

export const getReservationDate = createAction(
	'FETCH_RESERVATION_DATE',
	async (workDate, roomId) => {
		const { status, message, data } = await wrapFetchAuth(
			'reservation/getMonthStatus',
			{ method: 'GET' },
			{ work_date: workDate, room_id: roomId },
		);

		const reservationDate = {};

		data.forEach(item => {
			reservationDate[item.work_date] = item.status;
		});

		if (status !== 200) {
			throw openFailedAlert('generic', message);
		}

		return { reservationDate };
	},
);

export const getReservationDateWithInterview = createAction(
	'FETCH_RESERVATION_DATE',
	async workDate => {
		const { status, message, data } = await wrapFetchAuth(
			'reservation/getInterviewMonthStatus',
			{ method: 'GET' },
			{ work_date: workDate },
		);

		const reservationDate = {};

		data.forEach(item => {
			reservationDate[item.work_date] = item.status;
		});

		if (status !== 200) {
			throw openFailedAlert('generic', message);
		}

		return { reservationDate };
	},
);

export const getReservationDateWithOrder = createAction(
	'FETCH_RESERVATION_DATE',
	(orderId, type) => async dispatch => {
		const { status, message, data } = await wrapFetchAuth(
			'reservation/getOrderRecentDayStatus',
			{ method: 'GET' },
			{ order_id: orderId, event_type: type },
		);

		const reservationDate = {};
		let canReserveOrder = false;

		data.forEach(item => {
			reservationDate[item.work_date] = item.status;
			canReserveOrder = canReserveOrder || item.status;
		});

		if (!canReserveOrder) {
			if (type === 'CHECKIN') {
				dispatch(openFailedAlert('reserve', t('reservation:notReserveCheckinDate')));
			} else if (type === 'CHECKOUT') {
				dispatch(openFailedAlert('reserve', t('reservation:notReserveCheckoutDate')));
			}
		}

		if (status !== 200) {
			throw openFailedAlert('generic', message);
		}

		return { reservationDate };
	},
);

export const initReservationFormData = createAction('INIT_RESERVATION_FORM_DATA');

export const setReservationFormData = createAction(
	'SET_RESERVATION_FORM_DATA',
	(phone, hangout) => ({
		phone,
		hangout,
	}),
);

export const reserveProcess = createAction(
	'RESERVE_PROCESS',
	({ type, roomId, orderId, time }) => async (dispatch, getState) => {
		if (isEmpty(time)) {
			window.scrollTo(0, 0);
			throw openFailedAlert('reserve', t('reservation:notValidText'));
		}

		if (isEmpty(type)) {
			throw openFailedAlert('reserve', t('reservation:chooseTypeValid'));
		}

		const eventType = convertReservationEvent(type);
		const { reservation: { form: { phone, hangout } } } = getState();

		if (
			(['PHONE', 'LIVE'].includes(eventType) && isEmpty(phone.value) && !phone.validate) ||
			(eventType === 'ONLINE' && (isEmpty(hangout.value) || !isEmail(hangout.value)))
		) {
			window.scrollTo(0, 0);
			throw openFailedAlert('reserve', t('reservation:checkPhoneAndHangoutAlert'));
		}

		const data = {
			event_type: eventType,
			room_id: roomId,
			work_date: time.format('YYYY-MM-DD HH:mm:ss'),
		};

		if (['PHONE', 'LIVE'].includes(eventType)) {
			data.phone = phone.value;
			data.phone_country = phone.country.iso2;
		}

		if (['CHECKIN', 'CHECKOUT'].includes(eventType)) {
			data.order_id = orderId;
		}

		if (eventType === 'ONLINE') {
			data.hangout = hangout.value;
		}

		const { status } = await wrapFetchAuth('reservation/book', {
			method: 'POST',
			body: JSON.stringify(data),
		});

		if (status !== 200) {
			window.scrollTo(0, 0);
			throw openFailedAlert('reserve', t('reservation:notValidReserve'));
		}

		const { routing: { locationBeforeTransitions: { query, pathname } } } = getState();

		dispatch(getUser());
		dispatch(push({ pathname, query: { ...query, step: 'success' } }));

		return { type, time: time.valueOf(), canAccess: false };
	},
);

export const reserveInterviewProcess = createAction(
	'RESERVE_PROCESS',
	({ type, time }) => async (dispatch, getState) => {
		if (isEmpty(time)) {
			window.scrollTo(0, 0);
			throw openFailedAlert('reserve', t('reservation:notValidText'));
		}

		if (isEmpty(type)) {
			throw openFailedAlert('reserve', t('reservation:chooseTypeValid'));
		}

		const eventType = convertReservationEvent(type);
		const { reservation: { form: { phone, hangout } } } = getState();

		if (
			(eventType === 'INTERVIEW_PHONE' && isEmpty(phone.value) && !phone.validate) ||
			(eventType === 'INTERVIEW_ONLINE' && (isEmpty(hangout.value) || !isEmail(hangout.value)))
		) {
			window.scrollTo(0, 0);
			throw openFailedAlert('reserve', t('reservation:checkPhoneAndHangoutAlert'));
		}

		const data = {
			event_type: eventType,
			work_date: time.format('YYYY-MM-DD HH:mm:ss'),
		};

		if (eventType === 'INTERVIEW_PHONE') {
			data.phone = phone.value;
			data.phone_country = phone.country.iso2;
		}

		if (eventType === 'INTERVIEW_ONLINE') {
			data.hangout = hangout.value;
		}

		const { status } = await wrapFetchAuth('reservation/bookInterview', {
			method: 'POST',
			body: JSON.stringify(data),
		});

		if (status !== 200) {
			window.scrollTo(0, 0);
			throw openFailedAlert('reserve', t('reservation:notValidReserve'));
		}

		const { routing: { locationBeforeTransitions: { query, pathname } } } = getState();

		dispatch(getUser());
		dispatch(push({ pathname, query: { ...query, step: 'success' } }));

		return { type, time: time.valueOf(), canAccess: false };
	},
);

export const getReservation = createAction('FETCH_RESERVATION', async () => {
	const { status, data, message } = await wrapFetchAuth('reservation', { method: 'GET' });

	if (status !== 200) {
		throw openFailedAlert('generic', message);
	}

	const reservationData = {};
	const list = data.map(item => {
		reservationData[item.id] = item;

		return item.id;
	});

	return { list, data: reservationData };
});

export const getReservationById = createAction('FETCH_RESERVATION_BY_ID', async id => {
	const { status, data, message } = await wrapFetchAuth(`reservation/${id}`, { method: 'GET' });

	if (status !== 200) {
		throw openFailedAlert('generic', message);
	}

	return { data };
});

export const cancelReservation = createAction(
	'CANCEL_RESERVATION',
	(id, reason) => async (dispatch, getState) => {
		const { status, message } = await wrapFetchAuth(`reservation/${id}/cancel`, {
			method: 'POST',
			body: JSON.stringify({ note: reason }),
		});

		if (status !== 200) {
			// throw openFailedAlert(message);
			dispatch(openFailedAlert('reserveRecord', message));
		} else {
			dispatch(
				openSuccessAlert(
					'reserveRecord',
					t('reservation:cancelSuccess'),
				),
			);
		}
		const { i18n: { lan } } = getState();

		dispatch(push(`/${languageMapRoute(lan)}/user/reservation`));
	},
);

export const changeReservation = createAction(
	'CHANGE_RESERVE',
	({ reservationId, type, roomId, orderId, time }) => async (dispatch, getState) => {
		if (isEmpty(time)) {
			window.scrollTo(0, 0);
			throw openFailedAlert('reserve', t('reservation:notValidText'));
		}

		const eventType = convertReservationEvent(type);
		const { reservation: { form: { phone, hangout } } } = getState();

		if (
			(['PHONE', 'LIVE'].includes(eventType) && isEmpty(phone.value) && !phone.validate) ||
			(eventType === 'ONLINE' && (isEmpty(hangout.value) || !isEmail(hangout.value)))
		) {
			window.scrollTo(0, 0);
			throw openFailedAlert('reserve', t('reservation:checkPhoneAndHangoutAlert'));
		}

		const data = {
			event_type: eventType,
			room_id: roomId,
			work_date: time.format('YYYY-MM-DD HH:mm:ss'),
		};

		if (['PHONE', 'LIVE'].includes(eventType)) {
			data.phone = phone.value;
			data.phone_country = phone.country.iso2;
		}

		if (eventType === 'ONLINE') {
			data.hangout = hangout.value;
		}

		if (['CHECKIN', 'CHECKOUT'].includes(eventType)) {
			data.order_id = orderId;
		}

		const { status, message } = await wrapFetchAuth(`reservation/${reservationId}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});

		if (status !== 200) {
			window.scrollTo(0, 0);
			throw openFailedAlert('reserve', message);
		}

		dispatch(
			openSuccessAlert(
				'reserveRecord',
				t('reservation:updateSuccess'),
			),
		);

		const { i18n: { lan } } = getState();

		dispatch(push(`/${languageMapRoute(lan)}/user/reservation`));
	},
);

export const setAccessReserve = createAction('SET_ACCESS_RESERVE');



// WEBPACK FOOTER //
// ./src/actions/reservation.js